import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  Box,
  CircularProgress
} from '@mui/material';
import { clientInventoryAPI } from '../../services/api';

const DiscardDialog = ({ open, onClose, item, mode = 'create', onSuccess }) => {
  const [formData, setFormData] = useState({
    dateDiscarded: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Initialize form data when item changes
  useEffect(() => {
    if (item && mode === 'edit') {
      setFormData({
        dateDiscarded: item.dateDiscarded ? new Date(item.dateDiscarded).toISOString().split('T')[0] : '',
        notes: item.notes || ''
      });
    } else {
      setFormData({
        dateDiscarded: new Date().toISOString().split('T')[0], // Default to today
        notes: ''
      });
    }
    setErrors({});
    setSubmitError('');
  }, [item, mode, open]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.dateDiscarded) {
      newErrors.dateDiscarded = 'Date discarded is required';
    } else if (new Date(formData.dateDiscarded) > new Date()) {
      newErrors.dateDiscarded = 'Date cannot be in the future';
    }

    if (formData.notes && formData.notes.length > 5000) {
      newErrors.notes = 'Notes must be less than 5000 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError('');

      const payload = {
        dateDiscarded: formData.dateDiscarded,
        notes: formData.notes?.trim() || undefined
      };

      let response;
      if (mode === 'edit') {
        response = await clientInventoryAPI.updateTracking(item.tissueId, payload);
      } else {
        response = await clientInventoryAPI.markDiscarded(item.tissueId, payload);
      }

      if (response.success) {
        onSuccess();
        onClose();
      } else {
        setSubmitError(response.error || 'Failed to save discard information');
      }
    } catch (err) {
      console.error('Error saving discard information:', err);
      setSubmitError(err.message || 'An unexpected error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: undefined
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === 'edit' ? 'Edit Discard Information' : 'Mark Tissue ID as Discarded'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {mode === 'create' && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              This action will mark the vial as discarded. Please provide details below.
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Tissue ID: <strong>{item?.tissueId}</strong>
          </Typography>

          <TextField
            fullWidth
            label="Date Discarded *"
            type="date"
            value={formData.dateDiscarded}
            onChange={handleChange('dateDiscarded')}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: new Date().toISOString().split('T')[0] }}
            error={!!errors.dateDiscarded}
            helperText={errors.dateDiscarded || 'Date the vial was discarded'}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Notes (Optional but recommended)"
            multiline
            rows={4}
            value={formData.notes}
            onChange={handleChange('notes')}
            error={!!errors.notes}
            helperText={errors.notes || 'Please explain why this vial was discarded'}
            placeholder="e.g., Expired before use, damaged during shipping, contaminated, etc."
          />

          {submitError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {submitError}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="error"
          disabled={submitting}
          startIcon={submitting && <CircularProgress size={20} />}
        >
          {submitting ? 'Saving...' : mode === 'edit' ? 'Update' : 'Mark as Discarded'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DiscardDialog;

